export const DEFAULT_RETURN_URL = "/account";
export const STORE_ORIGIN = "https://www.ratandboa.com/";

/**
 * Sanitise a multipass `return_to` value.
 *
 * `value` is expected to have been decoded exactly once already (e.g. it came
 * out of `URLSearchParams.get()` / `URL.searchParams`). It is never encoded or
 * decoded again here: Shopify copies `return_to` verbatim into the redirect
 * `Location` header, and `JSON.stringify` handles escaping of the payload.
 *
 * Accepts only a same-site relative path ("/…", but not "//…") or an absolute
 * URL on the store origin, and rejects anything still carrying percent-encoded
 * "/" or ":" (a sign the value was encoded more than once upstream).
 * Falls back to "/account" otherwise.
 */
export const sanitiseReturnUrl = (value) => {
  if (typeof value !== "string" || value.trim() === "") {
    return DEFAULT_RETURN_URL;
  }

  const url = value.trim();

  if (/%2F|%3A/i.test(url)) {
    return DEFAULT_RETURN_URL;
  }

  const isRelative = url.startsWith("/") && !url.startsWith("//");
  const isStoreAbsolute = url.startsWith(STORE_ORIGIN);

  if (!isRelative && !isStoreAbsolute) {
    return DEFAULT_RETURN_URL;
  }

  return url;
};

/**
 * The Google SSO broker (login.ratandboa.com) round-trips `redirect_endpoint`
 * through its own session and re-serialises it on the way back, which has been
 * observed to add an extra layer of percent-encoding to a nested `return_url`
 * ("/" arrives back as a literal "%2F", which Shopify then copies verbatim into
 * the multipass redirect: https://www.ratandboa.com%2F).
 *
 * Rather than depend on the broker's encoding, keep the return URL on this
 * origin in sessionStorage for the duration of the SSO hop and send the broker
 * a fixed `redirect_endpoint` with no nested query string.
 */
export const SSO_RETURN_URL_KEY = "ssoReturnUrl";

export const stashSsoReturnUrl = (url) => {
  try {
    window.sessionStorage.setItem(SSO_RETURN_URL_KEY, sanitiseReturnUrl(url));
  } catch (e) {
    // sessionStorage unavailable (privacy mode / disabled); fall back to
    // the "/account" default on the callback.
  }
};

export const popSsoReturnUrl = () => {
  try {
    const url = window.sessionStorage.getItem(SSO_RETURN_URL_KEY);
    window.sessionStorage.removeItem(SSO_RETURN_URL_KEY);
    return url || null;
  } catch (e) {
    return null;
  }
};

const assignParams = (params, searchParams) => {
  searchParams.forEach(function (val, key) {
    if (params[key] !== undefined) {
      if (!Array.isArray(params[key])) {
        params[key] = [params[key]];
      }
      params[key].push(val);
    } else {
      params[key] = val;
    }
  });
};

export const getParams = (url = window.location) => {
  const params = {};
  const parsed = new URL(url, window.location.origin);

  assignParams(params, parsed.searchParams);

  // Account pages keep the route after the hash, so
  // /account#/refer-a-friend?ft-memberReferMobile=half never reaches searchParams.
  const hashQueryIndex = parsed.hash.indexOf("?");
  if (hashQueryIndex !== -1) {
    const hashParams = new URLSearchParams(
      parsed.hash.slice(hashQueryIndex + 1)
    );
    hashParams.forEach(function (val, key) {
      params[key] = val;
    });
  }

  return params;
};
